
import { Options, Vue as VueBase } from "vue-class-component";
import {
  convertDecimalWithComma,
  dateToDateString,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import formatByCountry from "@/app/infrastructures/misc/common-library/FormatByCountry";
import { HandoverController } from "@/app/ui/controllers/HandoverController";
import { Handover, RetryHandoverVendorResult } from "@/domain/entities/Handover";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { SubconsoleData } from "@/domain/entities/Subconsole";
import {
  RequestExportFileExcel,
  RequestListHandover
} from "@/data/payload/api/HandoverApiRequest";
import { FlagsPermissionHandover } from "@/feature-flags/flags-permission-handover";
import VendorStatusFailedModal from "./modules/VendorStatusFailedModal.vue";
import EligibilityFailedModal from "./modules/EligibilityFailedModal.vue";
import {
  getVendorOrderStatusBadgeStyle,
  getVendorOrderStatusLabel
} from "./modules/vendorOrderStatusLabel";
import {
  getHandoverStatusBadgeStyle,
  getHandoverStatusLabel
} from "./modules/handoverStatusLabel";
import { MainAppController } from "@/app/ui/controllers/MainAppController";

@Options({
  components: {
    VendorStatusFailedModal,
    EligibilityFailedModal
  }
})
class HandoverList extends VueBase {
  get getCurrentPath(): string {
    return this.$route.path;
  }
  get getTitle() {
    return this.$t("handover.list.title");
  }
  get ableToCreate() {
    if (this.getCurrentPath.startsWith("/handover-v2")) {
      return FlagsPermissionHandover.handover_scan_first_revamp.isEnabled();
    }

    return FlagsPermissionHandover.handover_create.isEnabled();
  }
  get ableToDetail() {
    return FlagsPermissionHandover.handover_detail_view.isEnabled();
  }
  mounted() {
    this.getListSubconsole();
  }
  unmounted() {
    HandoverController.setFilter(new RequestListHandover());
  }

  subconsole = "";

  showFailedVendorModal = false;
  selectedHandoverId = 0;
  eligibleRetryVendorStatus = false;

  openFailedVendorModal(handoverId: number, isRetryAvailable: boolean) {
    this.selectedHandoverId = handoverId;
    this.showFailedVendorModal = true;
    this.eligibleRetryVendorStatus = isRetryAvailable;
  }

  closeFailedVendorModal() {
    this.showFailedVendorModal = false;
    this.selectedHandoverId = 0;
    this.eligibleRetryVendorStatus = false;
  }

  // ---- Status handover (eligibility) ----
  showEligibilityModal = false;
  selectedEligibilityHandoverId = 0;

  openEligibilityModal(handoverId: number) {
    this.selectedEligibilityHandoverId = handoverId;
    this.showEligibilityModal = true;
  }

  closeEligibilityModal() {
    this.showEligibilityModal = false;
    this.selectedEligibilityHandoverId = 0;
  }

  handoverStatusBadgeStyle(status: string) {
    return getHandoverStatusBadgeStyle(status);
  }

  vendorStatusBadgeStyle(status: string) {
    return getVendorOrderStatusBadgeStyle(status);
  }

  // ---- Retry (Ulangi proses) ----
  showRetryProcess = false;
  retryProcessCount = 0;
  retryHandoverId = 0;

  get isRetrying() {
    return HandoverController.isRetrying;
  }

  async onClickRetry(handoverId: number) {
    this.closeFailedVendorModal();
    this.retryHandoverId = handoverId;

    if (!HandoverController.failedVendorSttByHandoverId[handoverId]) {
      try {
        await HandoverController.getFailedVendorStt(handoverId);
      } catch (_err) {
        return;
      }
    }

    const cache = HandoverController.failedVendorSttByHandoverId[handoverId];
    const eligibleStts = (cache?.stt || []).filter((stt) => stt.retryAvailable);
    const eligibleSttNos = eligibleStts.map((stt) => stt.sttNo);
    const n = eligibleSttNos.length;

    HandoverController.retryHandoverVendorOrder({
      handoverId,
      sttNo: eligibleSttNos[0] || "",
      sttNos: eligibleSttNos
    })
      .then((res: RetryHandoverVendorResult) => {
        HandoverController.handoverList.data = HandoverController.handoverList.data.map(
          (handover) => {
            if (handover.handoverId === handoverId) {
              return {
                ...handover,
                vendorOrderStatus: res.vendorOrderStatus,
                isRetryAvailable: false,
                isViewDetailVendorOrder: false
              };
            }
            return handover;
          }
        );
        this.retryProcessCount = n;
        this.showRetryProcess = true;
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            err,
            this.$t("handover.list.retry.failedToast"),
            () => this.onClickRetry(handoverId)
          )
        );
      });
  }

  closeRetryProcess() {
    this.showRetryProcess = false;
  }

  fetchHandoverList() {
    return HandoverController.fetchHandoverList();
  }

  // filter search
  get searchValue() {
    return HandoverController.filter.search;
  }
  onSearch(value: string) {
    HandoverController.setSearch(value);
    this.fetchHandoverList();
  }
  clearSearch() {
    HandoverController.setSearch("");
    this.fetchHandoverList();
  }

  get subConsoleList() {
    const res = [{ name: this.$t("handover.list.searchSubConsole"), value: 0 }];
    HandoverController.subconsoleList.data.forEach((key: SubconsoleData) => {
      res.push({ name: key.name, value: key.id });
    });
    return res;
  }

  getListSubconsole() {
    return HandoverController.getListSubconsole({
      parentId: Number(this.parentId)
    });
  }

  isOpenSelectSubConsole = false;
  onOpenSelectSubConsole() {
    this.isOpenSelectSubConsole = true;
  }

  onCloseSelectSubConsole() {
    this.isOpenSelectSubConsole = false;
  }

  onSelectSubConsole(name: string, value: string) {
    this.onCloseSelectSubConsole();
    this.resetPage();
    HandoverController.setSubConsoleId(value);
    this.fetchHandoverList();
  }

  // loading
  get isLoading() {
    return HandoverController.isLoading;
  }

  // error cause
  get errorCause() {
    return HandoverController.errorCause;
  }

  // filter date
  get startDate() {
    return HandoverController.filter.startDate;
  }

  get endDate() {
    return HandoverController.filter.endDate;
  }

  get parentId() {
    return AccountController.accountData.account_type_detail.id;
  }

  get destinationPartnerId(): any {
    return HandoverController.filter.destinationPartnerId;
  }

  setDateRange(value: Array<any>) {
    HandoverController.setFilter(
      new RequestListHandover({
        startDate: value[0],
        endDate: value[1]
      })
    );
    if (value[0] && value[1]) {
      this.resetPage();
      this.fetchHandoverList();
    }
  }

  get pagination() {
    return HandoverController.handoverList.pagination;
  }

  get handoverList() {
    return HandoverController.handoverList.data;
  }

  resetPage() {
    this.pagination.page = 1;
  }

  // Table
  get columns() {
    return [
      {
        name: this.$t("handover.list.table.no"),
        styleHead: "w-12 text-left whitespace-no-wrap font-bold",
        render: (_: Handover, index: number) => {
          return `<div class="overflow-ellipsis text-left font-semibold">${index +
            1 +
            this.pagination.limit * (this.pagination.page - 1)}</div>`;
        }
      },
      {
        name: this.$t("handover.list.table.idManifest"),
        styleHead: "w-32 text-left whitespace-no-wrap font-bold",
        render: (item: Handover) => {
          return `<div class="overflow-ellipsis text-left font-semibold">${item.handoverId}</div>`;
        }
      },
      {
        name: this.$t("handover.list.table.handoverTo"),
        styleHead: "w-64 text-left whitespace-no-wrap font-bold",
        render: (item: Handover) => {
          const vendorName =
            item.handoverVendorName ||
            (item.handoverDestinationPartnerName !== ""
              ? item.handoverDestinationPartnerName
              : item.handoverVendorCode) ||
            "—";
          return `<div class="overflow-ellipsis text-left font-semibold">${vendorName}</div>`;
        }
      },
      {
        name: this.$t("handover.list.table.stt"),
        styleHead: "w-28 text-left whitespace-no-wrap font-bold",
        render: (item: Handover) => {
          return `<div class="overflow-ellipsis text-left">${formatByCountry(item.handoverTotalSTT, { type: "quantity" })}</div>`;
        }
      },
      {
        name: this.$t("handover.list.table.pieces"),
        styleHead: "w-28 text-left whitespace-no-wrap font-bold",
        render: (item: Handover) => {
          return `<div class="overflow-ellipsis text-left">${formatByCountry(item.handoverTotalPiece, { type: "quantity" })}</div>`;
        }
      },
      {
        name: this.$t("handover.list.table.weight"),
        styleHead: "w-56 text-left whitespace-no-wrap font-bold",
        render: (item: Handover) => {
          return `<div class="overflow-ellipsis text-left" data-testid="outgoing-handover-total-gross-weight">${formatByCountry(item.handoverTotalGrossWeight, { type: "weight", suffix: " Kg" })}</div>`;
        }
      },
      {
        name: this.$t("handover.list.table.dimension"),
        styleHead: "w-56 text-left whitespace-no-wrap font-bold",
        render: (item: Handover) => {
          return `<div class="overflow-ellipsis text-left" data-testid="outgoing-handover-total-volume-weight">${formatByCountry(item.handoverTotalVolumeWeight, { type: "weight", suffix: " Kg" })}</div>`;
        }
      },
      {
        name: this.$t("handover.list.table.statusHandover"),
        columnKey: "handover_status",
        styleHead: "w-44 text-left whitespace-no-wrap font-bold",
        isUnderline: false,
        disableButtonValue: (item: Handover) => !item.isViewDetailHandover,
        onClickValue: (item: Handover) => {
          if (item.isViewDetailHandover) {
            this.openEligibilityModal(item.handoverId);
          }
        },
        render: (item: Handover) => {
          const label = getHandoverStatusLabel(item.handoverStatus, (key) =>
            this.$t(key)
          );
          if (label === "—") {
            return `<div class="overflow-ellipsis text-left text-gray-lp-500">—</div>`;
          }
          const badge = this.handoverStatusBadgeStyle(item.handoverStatus);
          const link = item.isViewDetailHandover
            ? `<div class="text-black-lp-100 underline cursor-pointer mt-1">${this.$t(
                "handover.list.table.viewDetail"
              )}</div>`
            : "";
          const icon = badge.icon
            ? `<img src="${badge.icon}" class="w-4 mr-1 inline-block" alt="circle-icon" />`
            : "";
          return `<div class="flex flex-col items-start">
              <div class="inline-flex items-center rounded-full px-2 py-0.5 font-medium ${badge.bg} ${badge.text}">
                ${icon}<span>${label}</span>
              </div>
              ${link}
            </div>`;
        }
      },
      {
        name: this.$t("handover.list.table.statusHitApi"),
        columnKey: "vendor_order_status",
        styleHead: "w-56 text-left whitespace-no-wrap font-bold",
        isUnderline: false,
        disableButtonValue: (item: Handover) => !item.isViewDetailVendorOrder,
        onClickValue: (item: Handover) => {
          if (item.isViewDetailVendorOrder) {
            this.openFailedVendorModal(item.handoverId, item.isRetryAvailable);
          }
        },
        render: (item: Handover) => {
          const label = getVendorOrderStatusLabel(
            item.vendorOrderStatus,
            (key) => this.$t(key)
          );
          if (label === "—") {
            return `<div class="overflow-ellipsis text-left text-gray-lp-500">—</div>`;
          }
          const badge = this.vendorStatusBadgeStyle(item.vendorOrderStatus);
          const link = item.isViewDetailVendorOrder
            ? `<div class="text-black-lp-100 underline cursor-pointer mt-1">${this.$t(
                "handover.list.table.viewDetail"
              )}</div>`
            : "";
          const icon = badge.icon
            ? `<img src="${badge.icon}" class="w-4 mr-1 inline-block" alt="circle-icon" />`
            : "";
          return `<div class="flex flex-col items-start">
              <div class="inline-flex items-center rounded-full px-2 py-0.5 font-medium ${badge.bg} ${badge.text}">
                ${icon}<span>${label}</span>
              </div>
              ${link}
            </div>`;
        }
      },
      {
        name: this.$t("handover.list.table.date"),
        styleHead: "w-64 text-left font-bold",
        render: (item: Handover) => {
          const operator = item.handoverCreatedBy
            ? `<div class="capitalize text-left text-gray-lp-500">${item.handoverCreatedBy}</div>`
            : "";
          return `<div class="text-black-lp-300">${this.formatDate(
            item.handoverCreatedAt
          )}</div>${operator}`;
        }
      },
      {
        name: this.$t("handover.list.table.action"),
        key: "button_column",
        styleHead: "w-24 text-left font-bold",
        styleCustom: "padding-action-sti",
        styleButton: (item: Handover) => {
          const groupOptions = [
            {
              icon: "print-summary",
              label: this.$t("handover.list.table.printSummary"),
              clickFunction: () => this.print(item.handoverId, "summary"),
              dataTestid: "handover-list-print-summary"
            },
            {
              icon: "printer",
              label: this.$t("handover.list.table.printPdf"),
              clickFunction: () => this.print(item.handoverId, "basic"),
              dataTestid: "handover-list-print-pdf"
            },
            {
              icon: "download-overlay",
              iconV2: true,
              label: this.$t("handover.list.table.download"),
              iconColor: "gray-lp-600",
              clickFunction: () => {
                const payload = new RequestExportFileExcel({
                  id: item.handoverId
                });
                HandoverController.exportExcelFile(payload);
              },
              dataTestid: "handover-list-download"
            }
          ];

          if (item.handoverIsSupportPrintResi) {
            groupOptions.push({
              icon: "printer",
              label: this.$t("handover.list.table.printVendor"),
              clickFunction: () =>
                this.printVendorReceipt(
                  item.handoverId,
                  item.handoverVendorCode
                ),
              dataTestid: "handover-list-print-receipt"
            });
          }

          if (item.isRetryAvailable) {
            groupOptions.unshift({
              icon: "ics_o_reload_gray",
              label: this.$t("handover.list.retry.menu"),
              clickFunction: () => this.onClickRetry(item.handoverId),
              dataTestid: "handover-list-retry"
            });
          }

          if (item.handoverStatus === "FAILED") {
            return {
              outline: true,
              icon: "option-gray",
              title: "",
              textColor: "black",
              color: "gray-lp-400",
              small: false,
              customClass: `w-full bg-gray-lp-1400 cursor-not-allowed`,
              clickFunction: () => null
            };
          }
          return {
            outline: true,
            icon: "option-gray",
            title: "",
            textColor: "black",
            color: "gray-lp-400",
            customClass: "button-option-sti",
            clickFunction: () => null,
            groupOptions: groupOptions
          };
        }
      }
    ];
  }

  async printVendorReceipt(handoverId: number, code = "") {
    const book: any = await import("./modules/print.vue");
    await book.default.methods.printVendorReceipt(handoverId, code);
  }

  async print(handoverId: number, type: string) {
    const book: any = await import("./modules/print.vue");
    const printType: any = {
      summary: () =>
        book.default.methods.printSummary(this.sourceImage, handoverId),
      basic: () => book.default.methods.print(this.sourceImage, handoverId)
    };

    printType[type]();
  }

  get sourceImage() {
    return (
      (AccountController.accountData.account_type === "partner"
        ? AccountController.accountData.account_type_detail.type
        : AccountController.accountData.account_type) || "internal"
    );
  }

  onClickRow(item: Handover) {
    this.$router.push(
      `${this.getCurrentPath}/${item.handoverId}?params=Handover Manifest - ${item.handoverId}`
    );
  }

  goToUpdate() {
    this.$router.push(`${this.getCurrentPath}/update`);
  }

  formatDate(date: string) {
    return dateToDateString(
      date,
      false,
      false,
      AccountController.accountData.accountIsForeign ? "en" : "id"
    );
  }
}
export default HandoverList;
